// frontend/src/pages/Reinsurance/components/Dashboard/index.js

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExclamationCircle, faSync, faChartLine, 
  faFileContract, faClipboardList, faMoneyBillWave,
  faArrowRight, faGlobe, faShieldAlt, faChartBar,
  faExclamationTriangle, faInfoCircle, faCalendarAlt,
  faUsers, faBuilding, faPlusCircle, faFileAlt,
  faWater, faFire
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { reinsuranceService } from '../../../../services/reinsurance';
import ReinsuranceCard from '../common/ReinsuranceCard';
import styles from './Dashboard.module.css';

const Dashboard = ({ onNavigate }) => {
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [exposureData, setExposureData] = useState(null);
  const [modelExposureData, setModelExposureData] = useState(null);
  const [riskDataLoading, setRiskDataLoading] = useState(false);
  const [riskDataError, setRiskDataError] = useState(null);
    
  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await reinsuranceService.getDashboardData();
      
      if (response && response.success) {
        setDashboardData(response.data);
      } else {
        throw new Error(response?.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Add a function to fetch risk data
  const fetchRiskData = useCallback(async () => {
    if (activeTab === 'risk-modeling') {
      setRiskDataLoading(true);
      setRiskDataError(null);
      
      try {
        // Fetch exposure data
        const exposureResponse = await reinsuranceService.getExposureData();
        if (exposureResponse.success) {
          setExposureData(exposureResponse.data);
        }
        
        // Fetch model exposure data for a sample contract
        // In a real app, you might want to fetch this for a specific contract
        const modelResponse = await reinsuranceService.modelExposure('c8f9d2e1-6b7a-4c5d-8e3f-2a1b0c9d8e7f');
        if (modelResponse.success) {
          setModelExposureData(modelResponse.data);
        }
      } catch (err) {
        console.error('Error fetching risk data:', err);
        setRiskDataError(err.message || 'Failed to load risk data. Please try again.');
      } finally {
        setRiskDataLoading(false);
      }
    }
  }, [activeTab]);

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Use useEffect to fetch risk data when the active tab changes
  useEffect(() => {
    if (activeTab === 'risk-modeling' && !exposureData) {
      fetchRiskData();
    }
  }, [activeTab, exposureData, fetchRiskData]);
  
  // Format currency
  function formatCurrency(amount, currency = 'USD', abbreviated = false) {
    if (amount === undefined || amount === null) return '';
    
    if (abbreviated && amount >= 1000000) {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: currency,
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 1
      }).format(amount);
    }
    
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
  
  // Format percentage
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '';
    return `${value}%`;
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Helper functions for colors and chart calculations
  function getColorForStatus(status) {
    switch (status) {
      case 'active': return 'var(--color-success)';
      case 'bound': return 'var(--color-info)';
      case 'draft': return 'var(--color-gray-400)';
      case 'expired': return 'var(--color-warning)';
      case 'cancelled': return 'var(--color-error)';
      default: return 'var(--color-primary)';
    }
  }

  function getColorForContractType(type) {
    switch (type) {
      case 'property': return 'var(--color-primary)';
      case 'specialty': return 'var(--color-success)';
      case 'parametric': return 'var(--color-info)';
      case 'ilw': return 'var(--color-warning)';
      case 'facultative': return 'var(--color-error)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getColorForEventType(type) {
    switch (type) {
      case 'hurricane': return 'var(--color-primary)';
      case 'earthquake': return 'var(--color-error)';
      case 'flood': return 'var(--color-info)';
      case 'wildfire': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getColorForClaimStatus(status) {
    switch (status) {
      case 'reported': return 'var(--color-warning)';
      case 'in_process': return 'var(--color-info)';
      case 'settled': return 'var(--color-success)';
      case 'closed': return 'var(--color-gray-400)';
      case 'disputed': return 'var(--color-error)';
      default: return 'var(--color-primary)';
    }
  }

  function getColorForInvestorType(type) {
    switch (type) {
      case 'ils_fund': return 'var(--color-primary)';
      case 'pension_fund': return 'var(--color-success)';
      case 'reinsurer': return 'var(--color-info)';
      case 'hedge_fund': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getColorForDomicile(domicile) {
    switch (domicile) {
      case 'Bermuda': return 'var(--color-primary)';
      case 'Singapore': return 'var(--color-success)';
      case 'United States': return 'var(--color-info)';
      case 'Switzerland': return 'var(--color-warning)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getIconForEventType(type) {
    switch (type) {
      case 'hurricane': return faExclamationTriangle;
      case 'earthquake': return faExclamationTriangle;
      case 'flood': return faWater;
      case 'wildfire': return faFire;
      default: return faExclamationTriangle;
    }
  }

  function getColorForPeril(peril) {
    switch (peril) {
      case 'hurricane': return 'var(--color-primary)';
      case 'earthquake': return 'var(--color-error)';
      case 'flood': return 'var(--color-info)';
      case 'wildfire': return 'var(--color-warning)';
      case 'tornado': return 'var(--color-purple)';
      case 'winter_storm': return 'var(--color-cyan)';
      case 'hail': return 'var(--color-teal)';
      case 'tsunami': return 'var(--color-indigo)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getColorForTerritory(territory) {
    switch (territory) {
      case 'US': return 'var(--color-primary)';
      case 'JP': return 'var(--color-error)';
      case 'CA': return 'var(--color-success)';
      case 'EU': return 'var(--color-info)';
      case 'AU': return 'var(--color-warning)';
      case 'NZ': return 'var(--color-purple)';
      case 'GLOBAL': return 'var(--color-gray-600)';
      default: return 'var(--color-gray-400)';
    }
  }

  function getIconForPeril(peril) {
    switch (peril) {
      case 'hurricane': return faExclamationTriangle;
      case 'earthquake': return faExclamationTriangle;
      case 'flood': return faWater;
      case 'wildfire': return faFire;
      case 'tornado': return faExclamationTriangle;
      case 'winter_storm': return faExclamationTriangle;
      case 'hail': return faExclamationTriangle;
      case 'tsunami': return faExclamationTriangle;
      default: return faExclamationTriangle;
    }
  }

  function getSegmentStart(data, key, index) {
    // Check if data contains objects with count property or direct values
    const hasCountProperty = Object.values(data)[0] && typeof Object.values(data)[0] === 'object' && 'count' in Object.values(data)[0];
    
    let totalValue = 0;
    if (hasCountProperty) {
      totalValue = Object.values(data).reduce((sum, item) => sum + item.count, 0);
    } else {
      totalValue = Object.values(data).reduce((sum, amount) => sum + amount, 0);
    }
    
    let startPercentage = 0;
    let i = 0;
    
    for (const [currentKey, value] of Object.entries(data)) {
      if (i >= index) break;
      
      if (hasCountProperty) {
        startPercentage += (value.count / totalValue) * 100;
      } else {
        startPercentage += (value / totalValue) * 100;
      }
      
      i++;
    }
    
    return `${startPercentage}%`;
  }

  function getSegmentEnd(data, key, index) {
    // Check if data contains objects with count property or direct values
    const hasCountProperty = Object.values(data)[0] && typeof Object.values(data)[0] === 'object' && 'count' in Object.values(data)[0];
    
    let totalValue = 0;
    if (hasCountProperty) {
      totalValue = Object.values(data).reduce((sum, item) => sum + item.count, 0);
    } else {
      totalValue = Object.values(data).reduce((sum, amount) => sum + amount, 0);
    }
    
    let startPercentage = 0;
    let endPercentage = 0;
    let i = 0;
    
    for (const [currentKey, value] of Object.entries(data)) {
      if (i > index) break;
      
      if (i < index) {
        if (hasCountProperty) {
          startPercentage += (value.count / totalValue) * 100;
        } else {
          startPercentage += (value / totalValue) * 100;
        }
      }
      
      if (hasCountProperty) {
        endPercentage = startPercentage + (value.count / totalValue) * 100;
      } else {
        endPercentage = startPercentage + (value / totalValue) * 100;
      }
      
      i++;
    }
    
    return `${endPercentage}%`;
  }
  
  if (isLoading && !dashboardData) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>{t('loading_dashboard_data')}</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.error}>
        <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
        <p>{error}</p>
        <button className={styles.retryButton} onClick={fetchDashboardData}>
          <FontAwesomeIcon icon={faSync} />
          <span>{t('retry')}</span>
        </button>
      </div>
    );
  }

  // Extract values from the data structure
  const activeContracts = dashboardData?.contract_metrics?.contracts_by_status?.active?.count || 0;
  const totalContracts = dashboardData?.contract_metrics?.total_contracts || 0;
  const totalExposure = dashboardData?.contract_metrics?.total_limit || 0;
  const totalPremium = dashboardData?.contract_metrics?.total_premium || 0;
  const openClaims = dashboardData?.claim_metrics?.total_claims || 0;
  const totalIncurred = dashboardData?.claim_metrics?.total_incurred || 0;
  const totalPaid = dashboardData?.claim_metrics?.total_paid || 0;
  const totalReserved = dashboardData?.claim_metrics?.total_reserved || 0;
  const capitalAllocated = dashboardData?.capital_metrics?.total_allocated || 0;
  const capitalAvailable = dashboardData?.capital_metrics?.total_available || 0;
  const allocationPercentage = dashboardData?.capital_metrics?.allocation_percentage || 0;
  
  // Calculate loss ratio
  const lossRatio = totalPremium > 0 ? ((totalIncurred / totalPremium) * 100).toFixed(1) : 0;
  
  // Calculate trends (we don't have this in our data structure, so we'll use placeholders)
  const activeContractsTrend = 5.2;
  const totalExposureTrend = 3.8;
  const openClaimsTrend = -12.5;
  const capitalAllocatedTrend = 8.3;
  
  // Mock data for cedents (based on the existing mock data structure)
  const cedentMetrics = {
    total_cedents: 5,
    active_cedents: 5,
    cedents_by_domicile: {
      "Bermuda": { count: 2 },
      "Singapore": { count: 1 },
      "United States": { count: 1 },
      "Switzerland": { count: 1 }
    }
  };
  
  // Mock data for investors (based on the capital_metrics in the existing mock data)
  const investorMetrics = {
    total_investors: dashboardData?.capital_metrics?.capital_by_investor_type 
      ? Object.values(dashboardData.capital_metrics.capital_by_investor_type).reduce((sum, type) => sum + type.count, 0) 
      : 0,
    total_capital: dashboardData?.capital_metrics?.total_capital || 0
  };
  
  return (
    <div className={styles.dashboard}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.title}>{t('reinsurance_dashboard')}</h1>
        <div className={styles.dashboardInfo}>
          <div className={styles.reportDate}>
            <FontAwesomeIcon icon={faCalendarAlt} className={styles.reportDateIcon} />
            <span>{t('report_date')}: {formatDate(dashboardData?.report_date)}</span>
          </div>
          <button className={styles.refreshButton} onClick={fetchDashboardData}>
            <FontAwesomeIcon icon={faSync} />
            <span>{t('refresh')}</span>
          </button>
        </div>
      </div>
      
      <div className={styles.tabs}>
        <button 
          className={`${styles.tabButton} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FontAwesomeIcon icon={faChartLine} />
          <span>{t('overview')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'contracts' ? styles.active : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          <FontAwesomeIcon icon={faFileContract} />
          <span>{t('contracts')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'claims' ? styles.active : ''}`}
          onClick={() => setActiveTab('claims')}
        >
          <FontAwesomeIcon icon={faClipboardList} />
          <span>{t('claims')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'capital' ? styles.active : ''}`}
          onClick={() => setActiveTab('capital')}
        >
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>{t('capital')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'risk-modeling' ? styles.active : ''}`}
          onClick={() => setActiveTab('risk-modeling')}
        >
          <FontAwesomeIcon icon={faShieldAlt} />
          <span>{t('risk_modeling')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'cedents' ? styles.active : ''}`}
          onClick={() => setActiveTab('cedents')}
        >
          <FontAwesomeIcon icon={faBuilding} />
          <span>{t('cedents')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'investors' ? styles.active : ''}`}
          onClick={() => setActiveTab('investors')}
        >
          <FontAwesomeIcon icon={faUsers} />
          <span>{t('investors')}</span>
        </button>
        <button 
          className={`${styles.tabButton} ${activeTab === 'reporting' ? styles.active : ''}`}
          onClick={() => setActiveTab('reporting')}
        >
          <FontAwesomeIcon icon={faFileAlt} />
          <span>{t('reporting')}</span>
        </button>
      </div>
      
      {activeTab === 'overview' && (
        <div className={styles.overviewTab}>
          <div className={styles.statsGrid}>
            <ReinsuranceCard
              title={t('active_contracts')}
              value={activeContracts}
              subtitle={`${t('of')} ${totalContracts} ${t('total')}`}
              trend={activeContractsTrend}
              icon="contract"
              onClick={() => onNavigate('contracts')}
              actionText={t('go_to_contract_management')}
              actionIcon={faArrowRight}
            />
            <ReinsuranceCard
              title={t('total_exposure')}
              value={totalExposure}
              subtitle={t('total_limit')}
              trend={totalExposureTrend}
              format="currency"
              icon="exposure"
              onClick={() => onNavigate('risk-modeling')}
              actionText={t('go_to_risk_modeling')}
              actionIcon={faArrowRight}
            />
            <ReinsuranceCard
              title={t('open_claims')}
              value={openClaims}
              subtitle={formatCurrency(totalIncurred)}
              trend={openClaimsTrend}
              icon="claim"
              status={openClaims > 0 ? 'warning' : 'success'}
              onClick={() => onNavigate('claims')}
              actionText={t('go_to_claim_management')}
              actionIcon={faArrowRight}
            />
            <ReinsuranceCard
              title={t('capital_allocated')}
              value={capitalAllocated}
              subtitle={`${formatPercentage(allocationPercentage)} ${t('of_total')}`}
              trend={capitalAllocatedTrend}
              format="currency"
              icon="capital"
              onClick={() => onNavigate('capital')}
              actionText={t('go_to_capital_management')}
              actionIcon={faArrowRight}
            />
            <ReinsuranceCard
              title={t('active_cedents')}
              value={cedentMetrics.active_cedents}
              subtitle={`${t('of')} ${cedentMetrics.total_cedents} ${t('total')}`}
              icon="cedent"
              onClick={() => onNavigate('cedents')}
              actionText={t('go_to_cedent_management')}
              actionIcon={faArrowRight}
            />
            <ReinsuranceCard
              title={t('total_investors')}
              value={investorMetrics.total_investors}
              subtitle={formatCurrency(investorMetrics.total_capital)}
              icon="investor"
              onClick={() => onNavigate('investors')}
              actionText={t('go_to_investor_management')}
              actionIcon={faArrowRight}
            />
          </div>
          
          <div className={styles.quickActions}>
            <h2 className={styles.sectionTitle}>{t('quick_actions')}</h2>
            <div className={styles.actionButtons}>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('cedents')}
              >
                <FontAwesomeIcon icon={faPlusCircle} className={styles.actionIcon} />
                <span>{t('create_cedent')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('contracts')}
              >
                <FontAwesomeIcon icon={faPlusCircle} className={styles.actionIcon} />
                <span>{t('create_contract')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('claims')}
              >
                <FontAwesomeIcon icon={faPlusCircle} className={styles.actionIcon} />
                <span>{t('start_claim')}</span>
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => onNavigate('capital')}
              >
                <FontAwesomeIcon icon={faPlusCircle} className={styles.actionIcon} />
                <span>{t('allocate_capital')}</span>
              </button>
            </div>
          </div>
          
          <div className={styles.summarySection}>
            <h2 className={styles.sectionTitle}>{t('portfolio_summary')}</h2>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('contract_types')}</h3>
                  <FontAwesomeIcon icon={faFileContract} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.contract_metrics?.contracts_by_type || {}).map(([type, data]) => (
                    <div className={styles.summaryItem} key={type}>
                      <div className={styles.summaryItemLabel}>{t(type)}</div>
                      <div className={styles.summaryItemValue}>{data.count}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.count / totalContracts) * 100}%`,
                            backgroundColor: getColorForContractType(type)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('claim_types')}</h3>
                  <FontAwesomeIcon icon={faClipboardList} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.claim_metrics?.claims_by_event_type || {}).map(([type, data]) => (
                    <div className={styles.summaryItem} key={type}>
                      <div className={styles.summaryItemLabel}>{t(type)}</div>
                      <div className={styles.summaryItemValue}>{data.count}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.count / openClaims) * 100}%`,
                            backgroundColor: getColorForEventType(type)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className={styles.summaryCard}>
                <div className={styles.summaryHeader}>
                  <h3 className={styles.summaryTitle}>{t('capital_allocation')}</h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} className={styles.summaryIcon} />
                </div>
                <div className={styles.summaryContent}>
                  {Object.entries(dashboardData?.capital_metrics?.capital_by_investor_type || {}).map(([type, data]) => (
                    <div className={styles.summaryItem} key={type}>
                      <div className={styles.summaryItemLabel}>{t(type)}</div>
                      <div className={styles.summaryItemValue}>{formatCurrency(data.total_allocated)}</div>
                      <div className={styles.summaryItemBar}>
                        <div 
                          className={styles.summaryItemProgress} 
                          style={{ 
                            width: `${(data.total_allocated / capitalAllocated) * 100}%`,
                            backgroundColor: getColorForInvestorType(type)
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('loss_ratio')}</h3>
                <FontAwesomeIcon icon={faChartLine} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatPercentage(lossRatio)}
              </p>
              <p className={styles.metricSubtitle}>
                {t('incurred_vs_premium')}
              </p>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('capital_available')}</h3>
                <FontAwesomeIcon icon={faMoneyBillWave} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatCurrency(capitalAvailable)}
              </p>
              <p className={styles.metricSubtitle}>
                {formatPercentage(100 - allocationPercentage)} {t('of_total_capital')}
              </p>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('average_premium')}</h3>
                <FontAwesomeIcon icon={faChartBar} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatCurrency(dashboardData?.contract_metrics?.average_premium || 0)}
              </p>
              <p className={styles.metricSubtitle}>
                {t('per_contract')}
              </p>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('claims_paid_ratio')}</h3>
                <FontAwesomeIcon icon={faClipboardList} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatPercentage(totalIncurred > 0 ? (totalPaid / totalIncurred) * 100 : 0)}
              </p>
              <p className={styles.metricSubtitle}>
                {formatCurrency(totalPaid)} / {formatCurrency(totalIncurred)}
              </p>
            </div>
          </div>
          
          <div className={styles.tablesGrid}>
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>{t('recent_contracts')}</h3>
                <button 
                  className={styles.viewAllButton}
                  onClick={() => onNavigate('contracts')}
                >
                  {t('view_all')}
                </button>
              </div>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('contract_number')}</th>
                      <th>{t('cedent')}</th>
                      <th>{t('type')}</th>
                      <th>{t('limit')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentContracts?.map(contract => (
                      <tr 
                        key={contract.contract_id}
                        onClick={() => onNavigate('contracts')}
                        className={styles.clickableRow}
                      >
                        <td>{contract.contract_number}</td>
                        <td>{contract.cedent.cedent_name}</td>
                        <td>{contract.contract_type}</td>
                        <td>{formatCurrency(contract.terms.limit)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[contract.performance.current_status]}`}>
                            {contract.performance.current_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className={styles.tableCard}>
              <div className={styles.tableHeader}>
                <h3 className={styles.tableTitle}>{t('recent_claims')}</h3>
                <button 
                  className={styles.viewAllButton}
                  onClick={() => onNavigate('claims')}
                >
                  {t('view_all')}
                </button>
              </div>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('claim_number')}</th>
                      <th>{t('event')}</th>
                      <th>{t('incurred_loss')}</th>
                      <th>{t('status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentClaims?.map(claim => (
                      <tr 
                        key={claim.claim_id}
                        onClick={() => onNavigate('claims')}
                        className={styles.clickableRow}
                      >
                        <td>{claim.claim_number}</td>
                        <td>{claim.event.event_name}</td>
                        <td>{formatCurrency(claim.financials.incurred_loss)}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[claim.status.current_status]}`}>
                            {claim.status.current_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'contracts' && (
        <div className={styles.contractsTab}>
          <div className={styles.contractsHeader}>
            <h2 className={styles.sectionTitle}>{t('contract_portfolio')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('contracts')}
            >
              <span>{t('manage_contracts')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.contractsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('contracts_by_status')}</h3>
                <FontAwesomeIcon icon={faInfoCircle} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <div className={styles.donutChartContainer}>
                  <div className={styles.donutChart}>
                    {Object.entries(dashboardData?.contract_metrics?.contracts_by_status || {}).map(([status, data], index) => (
                      <div 
                        key={status}
                        className={styles.donutSegment}
                        style={{
                          '--segment-start': getSegmentStart(dashboardData?.contract_metrics?.contracts_by_status || {}, status, index),
                          '--segment-end': getSegmentEnd(dashboardData?.contract_metrics?.contracts_by_status || {}, status, index),
                          '--segment-color': getColorForStatus(status)
                        }}
                      ></div>
                    ))}
                    <div className={styles.donutInner}>
                      <span className={styles.donutValue}>{totalContracts}</span>
                      <span className={styles.donutLabel}>{t('contracts')}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.legendContainer}>
                  {Object.entries(dashboardData?.contract_metrics?.contracts_by_status || {}).map(([status, data]) => (
                    <div className={styles.legendItem} key={status}>
                      <div 
                        className={styles.legendColor} 
                        style={{ backgroundColor: getColorForStatus(status) }}
                      ></div>
                      <div className={styles.legendLabel}>{t(status)}</div>
                      <div className={styles.legendValue}>{data.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('premium_by_contract_type')}</h3>
                <FontAwesomeIcon icon={faChartBar} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <div className={styles.barChartContainer}>
                  {Object.entries(dashboardData?.contract_metrics?.contracts_by_type || {}).map(([type, data]) => (
                    <div className={styles.barChartItem} key={type}>
                      <div className={styles.barLabel}>{t(type)}</div>
                      <div className={styles.barContainer}>
                        <div 
                          className={styles.bar}
                          style={{ 
                            width: `${(data.total_premium / totalPremium) * 100}%`,
                            backgroundColor: getColorForContractType(type)
                          }}
                        ></div>
                      </div>
                      <div className={styles.barValue}>{formatCurrency(data.total_premium)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className={styles.contractsList}>
            <h3 className={styles.listTitle}>{t('recent_contracts')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('contract_number')}</th>
                    <th>{t('cedent')}</th>
                    <th>{t('type')}</th>
                    <th>{t('effective_date')}</th>
                    <th>{t('expiration_date')}</th>
                    <th>{t('limit')}</th>
                    <th>{t('premium')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentContracts?.map(contract => (
                    <tr 
                      key={contract.contract_id}
                      onClick={() => onNavigate('contracts')}
                      className={styles.clickableRow}
                    >
                      <td>{contract.contract_number}</td>
                      <td>{contract.cedent.cedent_name}</td>
                      <td>{contract.contract_type}</td>
                      <td>{formatDate(contract.terms.effective_date)}</td>
                      <td>{formatDate(contract.terms.expiration_date)}</td>
                      <td>{formatCurrency(contract.terms.limit)}</td>
                      <td>{formatCurrency(contract.terms.premium.base_amount)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[contract.performance.current_status]}`}>
                          {contract.performance.current_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'claims' && (
        <div className={styles.claimsTab}>
          <div className={styles.claimsHeader}>
            <h2 className={styles.sectionTitle}>{t('claims_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('claims')}
            >
              <span>{t('manage_claims')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.claimsSummary}>
            <div className={styles.summaryCard}>
              <div className={styles.summaryHeader}>
                <h3 className={styles.summaryTitle}>{t('claims_by_status')}</h3>
                <FontAwesomeIcon icon={faClipboardList} className={styles.summaryIcon} />
              </div>
              <div className={styles.summaryContent}>
                {Object.entries(dashboardData?.claim_metrics?.claims_by_status || {}).map(([status, data]) => (
                  <div className={styles.summaryItem} key={status}>
                    <div className={styles.summaryItemLabel}>{t(status)}</div>
                    <div className={styles.summaryItemValue}>{data.count}</div>
                    <div className={styles.summaryItemBar}>
                      <div 
                        className={styles.summaryItemProgress} 
                        style={{ 
                          width: `${(data.count / openClaims) * 100}%`,
                          backgroundColor: getColorForClaimStatus(status)
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={styles.financialMetrics}>
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_incurred')}</h3>
                  <FontAwesomeIcon icon={faExclamationTriangle} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>
                  {formatCurrency(totalIncurred)}
                </p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_paid')}</h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>
                  {formatCurrency(totalPaid)}
                </p>
              </div>
              
              <div className={styles.metricCard}>
                <div className={styles.metricHeader}>
                  <h3 className={styles.metricTitle}>{t('total_reserved')}</h3>
                  <FontAwesomeIcon icon={faShieldAlt} className={styles.metricIcon} />
                </div>
                <p className={styles.metricValue}>
                  {formatCurrency(totalReserved)}
                </p>
              </div>
            </div>
          </div>
          
          <div className={styles.claimsByEvent}>
            <h3 className={styles.listTitle}>{t('claims_by_event_type')}</h3>
            <div className={styles.eventTypeGrid}>
              {Object.entries(dashboardData?.claim_metrics?.claims_by_event_type || {}).map(([type, data]) => (
                <div className={styles.eventTypeCard} key={type}>
                  <div className={styles.eventTypeIcon} style={{ backgroundColor: getColorForEventType(type) }}>
                    <FontAwesomeIcon icon={getIconForEventType(type)} />
                  </div>
                  <div className={styles.eventTypeInfo}>
                    <h4 className={styles.eventTypeName}>{t(type)}</h4>
                    <div className={styles.eventTypeStats}>
                      <div className={styles.eventTypeStat}>
                        <span className={styles.eventTypeLabel}>{t('claims')}</span>
                        <span className={styles.eventTypeValue}>{data.count}</span>
                      </div>
                      <div className={styles.eventTypeStat}>
                        <span className={styles.eventTypeLabel}>{t('incurred')}</span>
                        <span className={styles.eventTypeValue}>{formatCurrency(data.total_incurred)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.claimsList}>
            <h3 className={styles.listTitle}>{t('recent_claims')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('claim_number')}</th>
                    <th>{t('contract')}</th>
                    <th>{t('event')}</th>
                    <th>{t('event_date')}</th>
                    <th>{t('reported_date')}</th>
                    <th>{t('incurred_loss')}</th>
                    <th>{t('paid_to_date')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentClaims?.map(claim => (
                    <tr 
                      key={claim.claim_id}
                      onClick={() => onNavigate('claims')}
                      className={styles.clickableRow}
                    >
                      <td>{claim.claim_number}</td>
                      <td>{claim.contract_number}</td>
                      <td>{claim.event.event_name}</td>
                      <td>{formatDate(claim.event.event_date)}</td>
                      <td>{formatDate(claim.status.reported_date)}</td>
                      <td>{formatCurrency(claim.financials.incurred_loss)}</td>
                      <td>{formatCurrency(claim.financials.paid_to_date)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[claim.status.current_status]}`}>
                          {claim.status.current_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'capital' && (
        <div className={styles.capitalTab}>
          <div className={styles.capitalHeader}>
            <h2 className={styles.sectionTitle}>{t('capital_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('capital')}
            >
              <span>{t('manage_capital')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.capitalMetrics}>
            <div className={styles.capitalOverviewCard}>
              <div className={styles.capitalAllocationChart}>
                <div className={styles.pieChart}>
                  <div 
                    className={styles.pieSegment} 
                    style={{ 
                      '--segment-percentage': `${allocationPercentage}%`,
                      '--segment-color': 'var(--color-primary)'
                    }}
                  ></div>
                  <div className={styles.pieInner}>
                    <span className={styles.pieValue}>{formatPercentage(allocationPercentage)}</span>
                    <span className={styles.pieLabel}>{t('allocated')}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles.capitalStats}>
                <div className={styles.capitalStat}>
                  <div className={styles.capitalStatLabel}>{t('total_capital')}</div>
                  <div className={styles.capitalStatValue}>{formatCurrency(dashboardData?.capital_metrics?.total_capital || 0)}</div>
                </div>
                <div className={styles.capitalStat}>
                  <div className={styles.capitalStatLabel}>{t('allocated_capital')}</div>
                  <div className={styles.capitalStatValue}>{formatCurrency(capitalAllocated)}</div>
                </div>
                <div className={styles.capitalStat}>
                  <div className={styles.capitalStatLabel}>{t('available_capital')}</div>
                  <div className={styles.capitalStatValue}>{formatCurrency(capitalAvailable)}</div>
                </div>
              </div>
            </div>
            
            <div className={styles.investorTypeCard}>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{t('capital_by_investor_type')}</h3>
                <FontAwesomeIcon icon={faUsers} className={styles.cardIcon} />
              </div>
              <div className={styles.investorTypeContent}>
                {Object.entries(dashboardData?.capital_metrics?.capital_by_investor_type || {}).map(([type, data]) => (
                  <div className={styles.investorTypeItem} key={type}>
                    <div className={styles.investorTypeInfo}>
                      <div className={styles.investorTypeName}>{t(type)}</div>
                      <div className={styles.investorTypeCount}>{data.count} {t('investors')}</div>
                    </div>
                    <div className={styles.investorTypeAllocation}>
                      <div className={styles.investorTypeBar}>
                        <div 
                          className={styles.investorTypeProgress} 
                          style={{ 
                            width: `${(data.total_allocated / capitalAllocated) * 100}%`,
                            backgroundColor: getColorForInvestorType(type)
                          }}
                        ></div>
                      </div>
                      <div className={styles.investorTypeValues}>
                        <div className={styles.investorTypeAllocated}>{formatCurrency(data.total_allocated)}</div>
                        <div className={styles.investorTypeAvailable}>{formatCurrency(data.total_available)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className={styles.capitalAllocationByContract}>
            <h3 className={styles.listTitle}>{t('capital_allocation_by_contract')}</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('contract_number')}</th>
                    <th>{t('cedent')}</th>
                    <th>{t('type')}</th>
                    <th>{t('limit')}</th>
                    <th>{t('allocated_capital')}</th>
                    <th>{t('allocation_percentage')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData?.recentContracts?.slice(0, 5).map(contract => (
                    <tr 
                      key={contract.contract_id}
                      onClick={() => onNavigate('capital')}
                      className={styles.clickableRow}
                    >
                      <td>{contract.contract_number}</td>
                      <td>{contract.cedent.cedent_name}</td>
                      <td>{contract.contract_type}</td>
                      <td>{formatCurrency(contract.terms.limit)}</td>
                      <td>{formatCurrency(contract.terms.limit * 0.8)}</td>
                      <td>{formatPercentage(80)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[contract.performance.current_status]}`}>
                          {contract.performance.current_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'risk-modeling' && (
        <div className={styles.riskModelingTab}>
          <div className={styles.riskModelingHeader}>
            <h2 className={styles.sectionTitle}>{t('risk_modeling_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('risk-modeling')}
            >
              <span>{t('go_to_risk_modeling')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          {riskDataLoading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>{t('loading_risk_data')}</p>
            </div>
          ) : riskDataError ? (
            <div className={styles.error}>
              <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
              <p>{riskDataError}</p>
              <button className={styles.retryButton} onClick={fetchRiskData}>
                <FontAwesomeIcon icon={faSync} />
                <span>{t('retry')}</span>
              </button>
            </div>
          ) : exposureData && modelExposureData ? (
            <>
              <div className={styles.exposureOverview}>
                <div className={styles.overviewCard}>
                  <div className={styles.overviewHeader}>
                    <h3 className={styles.overviewTitle}>{t('exposure_by_peril')}</h3>
                    <FontAwesomeIcon icon={faShieldAlt} className={styles.overviewIcon} />
                  </div>
                  <div className={styles.overviewContent}>
                    <div className={styles.donutChartContainer}>
                      <div className={styles.donutChart}>
                        {Object.entries(exposureData.exposure_by_peril).map(([peril, amount], index) => (
                          <div 
                            key={peril}
                            className={styles.donutSegment}
                            style={{
                              '--segment-start': getSegmentStart(exposureData.exposure_by_peril, peril, index),
                              '--segment-end': getSegmentEnd(exposureData.exposure_by_peril, peril, index),
                              '--segment-color': getColorForPeril(peril)
                            }}
                          ></div>
                        ))}
                        <div className={styles.donutInner}>
                          <span className={styles.donutValue}>{formatCurrency(exposureData.total_exposure, 'USD', true)}</span>
                          <span className={styles.donutLabel}>{t('total')}</span>
                        </div>
                      </div>
                    </div>
                    <div className={styles.legendContainer}>
                      {Object.entries(exposureData.exposure_by_peril).map(([peril, amount]) => (
                        <div className={styles.legendItem} key={peril}>
                          <div 
                            className={styles.legendColor} 
                            style={{ backgroundColor: getColorForPeril(peril) }}
                          ></div>
                          <div className={styles.legendLabel}>{t(peril)}</div>
                          <div className={styles.legendValue}>{formatCurrency(amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className={styles.overviewCard}>
                  <div className={styles.overviewHeader}>
                    <h3 className={styles.overviewTitle}>{t('exposure_by_territory')}</h3>
                    <FontAwesomeIcon icon={faGlobe} className={styles.overviewIcon} />
                  </div>
                  <div className={styles.overviewContent}>
                    <div className={styles.barChartContainer}>
                      {Object.entries(exposureData.exposure_by_territory).map(([territory, amount]) => (
                        <div className={styles.barChartItem} key={territory}>
                          <div className={styles.barLabel}>{territory}</div>
                          <div className={styles.barContainer}>
                            <div 
                              className={styles.bar}
                              style={{ 
                                width: `${(amount / exposureData.total_exposure) * 100}%`,
                                backgroundColor: getColorForTerritory(territory)
                              }}
                            ></div>
                          </div>
                          <div className={styles.barValue}>{formatCurrency(amount)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={styles.riskMetrics}>
                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <h3 className={styles.metricTitle}>{t('total_exposure')}</h3>
                    <FontAwesomeIcon icon={faChartLine} className={styles.metricIcon} />
                  </div>
                  <p className={styles.metricValue}>
                    {formatCurrency(exposureData.total_exposure)}
                  </p>
                </div>
                
                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <h3 className={styles.metricTitle}>{t('probable_maximum_loss')}</h3>
                    <FontAwesomeIcon icon={faExclamationTriangle} className={styles.metricIcon} />
                  </div>
                  <p className={styles.metricValue}>
                    {formatCurrency(modelExposureData.modeling_results.exceedance_probability['1_in_100'])}
                  </p>
                  <p className={styles.metricSubtitle}>
                    {t('1_in_100_year_event')}
                  </p>
                </div>
                
                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <h3 className={styles.metricTitle}>{t('expected_loss')}</h3>
                    <FontAwesomeIcon icon={faChartBar} className={styles.metricIcon} />
                  </div>
                  <p className={styles.metricValue}>
                    {formatCurrency(modelExposureData.modeling_results.expected_loss)}
                  </p>
                </div>
                
                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <h3 className={styles.metricTitle}>{t('tail_value_at_risk')}</h3>
                    <FontAwesomeIcon icon={faChartLine} className={styles.metricIcon} />
                  </div>
                  <p className={styles.metricValue}>
                    {formatCurrency(modelExposureData.modeling_results.tail_value_at_risk)}
                  </p>
                </div>
              </div>
              
              <div className={styles.modelingResults}>
                <h3 className={styles.listTitle}>{t('exceedance_probability_curve')}</h3>
                <div className={styles.exceedanceCurve}>
                  <div className={styles.curveContainer}>
                    <div className={styles.curvePlaceholder}>
                      <span>{t('exceedance_curve_visualization')}</span>
                    </div>
                  </div>
                  <div className={styles.curveTable}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>{t('return_period')}</th>
                          <th>{t('loss_amount')}</th>
                          <th>{t('probability')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(modelExposureData.modeling_results.exceedance_probability).map(([returnPeriod, value]) => (
                          <tr key={returnPeriod}>
                            <td>{returnPeriod.replace(/_/g, ' ')}</td>
                            <td>{formatCurrency(value)}</td>
                            <td>{formatPercentage(100 / parseInt(returnPeriod.split('_')[2]))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div className={styles.perilAnalysis}>
                <h3 className={styles.listTitle}>{t('peak_exposures')}</h3>
                <div className={styles.peakExposures}>
                  {exposureData.peak_exposures.map((exposure, index) => (
                    <div className={styles.peakExposureCard} key={index}>
                      <div className={styles.peakExposureIcon} style={{ backgroundColor: getColorForPeril(exposure.peril) }}>
                        <FontAwesomeIcon icon={getIconForPeril(exposure.peril)} />
                      </div>
                      <div className={styles.peakExposureInfo}>
                        <h4 className={styles.peakExposureName}>
                          {t(exposure.peril)} - {exposure.territory}
                        </h4>
                        <div className={styles.peakExposureValue}>
                          {formatCurrency(exposure.amount)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.emptyState}>
              <p>{t('no_risk_data_available')}</p>
              <button className={styles.retryButton} onClick={fetchRiskData}>
                <FontAwesomeIcon icon={faSync} />
                <span>{t('load_risk_data')}</span>
              </button>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'cedents' && (
        <div className={styles.cedentsTab}>
          <div className={styles.cedentsHeader}>
            <h2 className={styles.sectionTitle}>{t('cedents_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('cedents')}
            >
              <span>{t('manage_cedents')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.cedentsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('cedents_by_domicile')}</h3>
                <FontAwesomeIcon icon={faGlobe} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <div className={styles.donutChartContainer}>
                  <div className={styles.donutChart}>
                    {Object.entries(cedentMetrics.cedents_by_domicile).map(([domicile, data], index) => (
                      <div 
                        key={domicile}
                        className={styles.donutSegment}
                        style={{
                          '--segment-start': getSegmentStart(cedentMetrics.cedents_by_domicile, domicile, index),
                          '--segment-end': getSegmentEnd(cedentMetrics.cedents_by_domicile, domicile, index),
                          '--segment-color': getColorForDomicile(domicile)
                        }}
                      ></div>
                    ))}
                    <div className={styles.donutInner}>
                      <span className={styles.donutValue}>{cedentMetrics.total_cedents}</span>
                      <span className={styles.donutLabel}>{t('cedents')}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.legendContainer}>
                  {Object.entries(cedentMetrics.cedents_by_domicile).map(([domicile, data]) => (
                    <div className={styles.legendItem} key={domicile}>
                      <div 
                        className={styles.legendColor} 
                        style={{ backgroundColor: getColorForDomicile(domicile) }}
                      ></div>
                      <div className={styles.legendLabel}>{domicile}</div>
                      <div className={styles.legendValue}>{data.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className={styles.cedentsList}>
              <h3 className={styles.listTitle}>{t('cedent_list')}</h3>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('cedent_name')}</th>
                      <th>{t('domicile')}</th>
                      <th>{t('active_contracts')}</th>
                      <th>{t('total_premium')}</th>
                      <th>{t('am_best_rating')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData?.recentContracts?.slice(0, 5).map(contract => (
                      <tr 
                        key={contract.contract_id}
                        onClick={() => onNavigate('cedents')}
                        className={styles.clickableRow}
                      >
                        <td>{contract.cedent.cedent_name}</td>
                        <td>{contract.cedent.cedent_domicile || "Bermuda"}</td>
                        <td>1</td>
                        <td>{formatCurrency(contract.terms.premium.base_amount)}</td>
                        <td>A</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'investors' && (
        <div className={styles.investorsTab}>
          <div className={styles.investorsHeader}>
            <h2 className={styles.sectionTitle}>{t('investors_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('investors')}
            >
              <span>{t('manage_investors')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.investorsOverview}>
            <div className={styles.overviewCard}>
              <div className={styles.overviewHeader}>
                <h3 className={styles.overviewTitle}>{t('investors_by_type')}</h3>
                <FontAwesomeIcon icon={faUsers} className={styles.overviewIcon} />
              </div>
              <div className={styles.overviewContent}>
                <div className={styles.donutChartContainer}>
                  <div className={styles.donutChart}>
                    {Object.entries(dashboardData?.capital_metrics?.capital_by_investor_type || {}).map(([type, data], index) => (
                      <div 
                        key={type}
                        className={styles.donutSegment}
                        style={{
                          '--segment-start': getSegmentStart(dashboardData?.capital_metrics?.capital_by_investor_type || {}, type, index),
                          '--segment-end': getSegmentEnd(dashboardData?.capital_metrics?.capital_by_investor_type || {}, type, index),
                          '--segment-color': getColorForInvestorType(type)
                        }}
                      ></div>
                    ))}
                    <div className={styles.donutInner}>
                      <span className={styles.donutValue}>{investorMetrics.total_investors}</span>
                      <span className={styles.donutLabel}>{t('investors')}</span>
                    </div>
                  </div>
                </div>
                <div className={styles.legendContainer}>
                  {Object.entries(dashboardData?.capital_metrics?.capital_by_investor_type || {}).map(([type, data]) => (
                    <div className={styles.legendItem} key={type}>
                      <div 
                        className={styles.legendColor} 
                        style={{ backgroundColor: getColorForInvestorType(type) }}
                      ></div>
                      <div className={styles.legendLabel}>{t(type)}</div>
                      <div className={styles.legendValue}>{data.count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className={styles.investorsList}>
              <h3 className={styles.listTitle}>{t('investor_list')}</h3>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('investor_name')}</th>
                      <th>{t('investor_type')}</th>
                      <th>{t('total_capital')}</th>
                      <th>{t('allocated_capital')}</th>
                      <th>{t('available_capital')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(dashboardData?.capital_metrics?.capital_by_investor_type || {}).map(([type, data]) => (
                      <tr 
                        key={type}
                        onClick={() => onNavigate('investors')}
                        className={styles.clickableRow}
                      >
                        <td>{`${t(type)} Fund`}</td>
                        <td>{t(type)}</td>
                        <td>{formatCurrency(data.total_capital)}</td>
                        <td>{formatCurrency(data.total_allocated)}</td>
                        <td>{formatCurrency(data.total_available)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'reporting' && (
        <div className={styles.reportingTab}>
          <div className={styles.reportingHeader}>
            <h2 className={styles.sectionTitle}>{t('reporting_overview')}</h2>
            <button 
              className={styles.actionButton}
              onClick={() => onNavigate('reporting')}
            >
              <span>{t('go_to_reporting')}</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.reportingCards}>
            <div className={styles.reportCard} onClick={() => onNavigate('reporting')}>
              <div className={styles.reportCardIcon}>
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className={styles.reportCardContent}>
                <h3 className={styles.reportCardTitle}>{t('portfolio_performance')}</h3>
                <p className={styles.reportCardDescription}>{t('portfolio_performance_description')}</p>
              </div>
            </div>
            
            <div className={styles.reportCard} onClick={() => onNavigate('reporting')}>
              <div className={styles.reportCardIcon}>
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div className={styles.reportCardContent}>
                <h3 className={styles.reportCardTitle}>{t('investor_reports')}</h3>
                <p className={styles.reportCardDescription}>{t('investor_reports_description')}</p>
              </div>
            </div>
            
            <div className={styles.reportCard} onClick={() => onNavigate('reporting')}>
              <div className={styles.reportCardIcon}>
                <FontAwesomeIcon icon={faFileAlt} />
              </div>
              <div className={styles.reportCardContent}>
                <h3 className={styles.reportCardTitle}>{t('regulatory_reports')}</h3>
                <p className={styles.reportCardDescription}>{t('regulatory_reports_description')}</p>
              </div>
            </div>
          </div>
          
          <div className={styles.reportingMetrics}>
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('loss_ratio')}</h3>
                <FontAwesomeIcon icon={faChartLine} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatPercentage(lossRatio)}
              </p>
              <p className={styles.metricSubtitle}>
                {t('incurred_vs_premium')}
              </p>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('combined_ratio')}</h3>
                <FontAwesomeIcon icon={faChartBar} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatPercentage(parseFloat(lossRatio) + 30)} {/* Mocked combined ratio */}
              </p>
              <p className={styles.metricSubtitle}>
                {t('loss_ratio_plus_expenses')}
              </p>
            </div>
            
            <div className={styles.metricCard}>
              <div className={styles.metricHeader}>
                <h3 className={styles.metricTitle}>{t('return_on_capital')}</h3>
                <FontAwesomeIcon icon={faMoneyBillWave} className={styles.metricIcon} />
              </div>
              <p className={styles.metricValue}>
                {formatPercentage(10.1)} {/* Mocked ROC from the data */}
              </p>
              <p className={styles.metricSubtitle}>
                {t('annualized')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;